import React, { useEffect, useState } from "react";
import {
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
} from "@material-ui/core";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import DeleteSweepOutlinedIcon from "@material-ui/icons/DeleteSweepOutlined";
import * as api from "../../api";
import { useSnackbar } from "../../utils/useSnackbar";

// Same preset list as GlobalSettingsTab.js's Activity Logs card - kept as
// its own copy rather than a shared import so this small, SuperAdmin-only
// widget doesn't pull in that whole settings-tab module.
const RETENTION_PRESETS = [30, 60, 90, 120, 150, 200];

// SuperAdmin-only control surfaced directly on the Activity Logs page
// itself, not just buried in Global Settings - this is where a SuperAdmin
// is actually looking at logs and likely to notice/care that the database
// is filling up, so the retention window and a way to change or purge it
// belong right here too. Renders nothing until the current setting has
// loaded, and nothing at all if that load fails (settings stay editable
// from Global Settings either way).
const ProgramLogRetentionControl = () => {
  const [openSnackbar] = useSnackbar();
  const [retentionDays, setRetentionDays] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftDays, setDraftDays] = useState(30);
  const [saving, setSaving] = useState(false);
  const [purging, setPurging] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.getGlobalSettings();
        if (!cancelled) setRetentionDays(Number(data?.data?.programLogRetentionDays) || 30);
      } catch (error) {
        // Non-fatal - the chip just stays hidden; Global Settings is still
        // the fallback place to manage this.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const openDialog = () => {
    setDraftDays(retentionDays || 30);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const days = Number(draftDays);
    if (!Number.isInteger(days) || days < 1 || days > 1825) {
      openSnackbar("Retention must be a whole number of days between 1 and 1825");
      return;
    }
    setSaving(true);
    try {
      await api.updateGlobalSettings({ programLogRetentionDays: days });
      setRetentionDays(days);
      openSnackbar(`Log retention updated to ${days} day(s)`);
      setDialogOpen(false);
    } catch (error) {
      openSnackbar(error?.response?.data?.message || "Failed to update retention");
    } finally {
      setSaving(false);
    }
  };

  const handlePurgeNow = async () => {
    const days = Number(draftDays) || retentionDays || 30;
    if (
      !window.confirm(
        `This permanently deletes every Activity Log row older than ${days} day(s), for every store. This cannot be undone. Continue?`
      )
    ) {
      return;
    }
    setPurging(true);
    try {
      // Purge reads the retention window from the saved setting, not this
      // dialog's draft - persist a changed value first so it's actually
      // what gets applied (same reasoning as GlobalSettingsTab.js's own
      // Purge Now button).
      if (days !== retentionDays) {
        await api.updateGlobalSettings({ programLogRetentionDays: days });
        setRetentionDays(days);
      }
      const { data } = await api.purgeProgramLogs();
      const suffix = data?.truncated ? " - more remain, click Purge again to continue" : "";
      openSnackbar(`Deleted ${data?.deletedCount ?? 0} log(s) older than ${data?.retentionDays ?? days} day(s)${suffix}`);
    } catch (error) {
      openSnackbar(error?.response?.data?.message || "Failed to purge old logs");
    } finally {
      setPurging(false);
    }
  };

  if (retentionDays == null) return null;

  return (
    <>
      <Tooltip title="Log rows older than this are deleted automatically every night, for every store - click to change it or purge now" arrow>
        <Chip
          icon={<DeleteSweepOutlinedIcon style={{ fontSize: 16 }} />}
          label={`Auto-cleanup: ${retentionDays}d`}
          onClick={openDialog}
          clickable
          size="small"
          style={{ fontWeight: 700, backgroundColor: "#f1f5f9", color: "#334155", border: "1px solid #e2e8f0" }}
        />
      </Tooltip>

      <Dialog open={dialogOpen} onClose={() => !saving && !purging && setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Activity Log Retention</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ color: "#64748b", marginBottom: 14, lineHeight: 1.5 }}>
            Log rows older than this window are auto-deleted every night at 3 AM - this is what keeps the
            shared database from filling up over time. Applies to every store.
          </Typography>

          <div
            style={{
              background: "#f1f5f9",
              padding: 4,
              borderRadius: 12,
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              marginBottom: 14,
            }}
          >
            <ToggleButtonGroup
              value={RETENTION_PRESETS.includes(Number(draftDays)) ? Number(draftDays) : null}
              exclusive
              onChange={(e, val) => val != null && setDraftDays(val)}
              size="small"
              style={{ width: "100%", display: "flex", flexWrap: "wrap" }}
            >
              {RETENTION_PRESETS.map((d) => (
                <ToggleButton
                  key={d}
                  value={d}
                  disabled={saving || purging}
                  style={{
                    flex: 1,
                    minWidth: 50,
                    borderRadius: 9,
                    padding: "7px 6px",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    textTransform: "none",
                    border: "none",
                    backgroundColor: Number(draftDays) === d ? "#ffffff" : "transparent",
                    color: Number(draftDays) === d ? "#ea580c" : "#64748b",
                    boxShadow: Number(draftDays) === d ? "0 2px 6px rgba(0,0,0,0.06)" : "none",
                  }}
                >
                  {d}d
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </div>

          <TextField
            label="Custom (days)"
            type="number"
            size="small"
            variant="outlined"
            fullWidth
            value={draftDays}
            onChange={(e) => setDraftDays(e.target.value === "" ? "" : Number(e.target.value))}
            disabled={saving || purging}
            inputProps={{ min: 1, max: 1825 }}
          />
        </DialogContent>
        <DialogActions style={{ padding: "12px 20px", justifyContent: "space-between" }}>
          <Button
            onClick={handlePurgeNow}
            disabled={saving || purging}
            style={{ color: "#b91c1c", textTransform: "none", fontWeight: 700 }}
          >
            {purging ? "Purging…" : "Purge Now"}
          </Button>
          <div>
            <Button onClick={() => setDialogOpen(false)} disabled={saving || purging} style={{ textTransform: "none" }}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || purging}
              variant="contained"
              color="primary"
              style={{ textTransform: "none", fontWeight: 700, marginLeft: 8 }}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProgramLogRetentionControl;
